import { useUploadFiles } from '@newfront-insurance/admin-ui/files';
import { CHAT_ALLOWED_ATTACHMENT_EXTENSIONS_LIST } from '@newfront-insurance/ai-api';
import { isNotEmpty } from '@newfront-insurance/core';
import { useAnalytics } from '@newfront-insurance/data-layer-client';
import { browserLogger } from '@newfront-insurance/dd-rum';
import { useNotifications } from '@newfront-insurance/next-notifications';
import { useCallback, useRef, useState, type Dispatch, type SetStateAction } from 'react';

import {
  buildLocalFolderArchiveFilename,
  buildLocalFolderBundle,
  LocalFolderPickerUnsupportedError,
  pickLocalFolderDirectory,
  type LocalFolderBundleMetadata,
} from './local-folder-bundle';
import { getFileAnalyticsProperties, NOVA_ANALYTICS_EVENTS, trackNovaEvent } from '../components/chat-v2/lib/analytics';

// Max file size based on OpenAI file size limit
export const MAX_FILE_SIZE_MB = 512;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

type FileAttachmentSource = 'button' | 'drag-drop' | 'paste' | 'folder-picker';

export interface FileAttachment {
  file: File;
  fileUuid?: string;
  kind?: 'file' | 'folder';
  folderName?: string;
  folderMetadata?: LocalFolderBundleMetadata;
  isUploading: boolean;
  processingStage?: 'preparing' | 'uploading';
  uploadError?: string;
  tempId: string; // Unique identifier for tracking during upload
}

interface UploadAttachmentOptions {
  attachment: FileAttachment;
  attachmentStateMode?: 'append' | 'replace';
  file: File;
  source: FileAttachmentSource;
  tempId: string;
}

interface UseFileSelectionOptions {
  setAttachments: Dispatch<SetStateAction<Array<FileAttachment>>>;
  showNotifications?: boolean; // Whether to show error notifications, defaults to true
  chatUuid?: string;
}

export function useFileSelection({ setAttachments, showNotifications = true, chatUuid }: UseFileSelectionOptions) {
  const { add: addNotification } = useNotifications();
  const analytics = useAnalytics();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const { upload, uploadProgress } = useUploadFiles();

  const uploadFileAttachment = useCallback(
    async ({ attachment, attachmentStateMode = 'append', file, source, tempId }: UploadAttachmentOptions) => {
      trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.CHAT_FILE_ATTACHMENT_STARTED, {
        chatUuid,
        source,
        ...getFileAnalyticsProperties(file),
      });

      setAttachments((existingAttachments) =>
        attachmentStateMode === 'replace'
          ? existingAttachments.map((existingAttachment) =>
              existingAttachment.tempId === tempId ? attachment : existingAttachment,
            )
          : [...existingAttachments, attachment],
      );

      browserLogger.info(`[Nova] Starting individual file upload`, {
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        tempId,
        attachmentKind: attachment.kind ?? 'file',
      });

      try {
        const response = await upload({
          files: [
            {
              rawFile: file,
              category: 'AI_CHAT_ATTACHMENT',
            },
          ],
        });

        const fileUuid = response[0]?.id;
        browserLogger.info(`[Nova] File upload completed`, {
          fileName: file.name,
          tempId,
          fileUuid,
          success: !!fileUuid,
          attachmentKind: attachment.kind ?? 'file',
        });

        if (fileUuid) {
          trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.CHAT_FILE_ATTACHED, {
            chatUuid,
            fileUuid,
            source,
            ...getFileAnalyticsProperties(file),
          });
        } else {
          trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.CHAT_FILE_ATTACHMENT_FAILED, {
            chatUuid,
            reason: 'missing_file_uuid',
            source,
            ...getFileAnalyticsProperties(file),
          });
        }

        setAttachments((prev) =>
          prev.map((existingAttachment) =>
            existingAttachment.tempId === tempId
              ? {
                  ...existingAttachment,
                  fileUuid,
                  isUploading: false,
                  processingStage: undefined,
                  uploadError: fileUuid ? undefined : 'Upload failed',
                }
              : existingAttachment,
          ),
        );
      } catch (error) {
        trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.CHAT_FILE_ATTACHMENT_FAILED, {
          chatUuid,
          errorName: error instanceof Error ? error.name : undefined,
          reason: 'upload_error',
          source,
          ...getFileAnalyticsProperties(file),
        });

        browserLogger.error(`[Nova] File upload failed`, {
          fileName: file.name,
          fileSize: file.size,
          tempId,
          error: error instanceof Error ? error.message : 'Unknown error',
          errorStack: error instanceof Error ? error.stack : undefined,
          attachmentKind: attachment.kind ?? 'file',
        });

        setAttachments((prev) =>
          prev.map((existingAttachment) =>
            existingAttachment.tempId === tempId
              ? {
                  ...existingAttachment,
                  isUploading: false,
                  processingStage: undefined,
                  uploadError: 'Upload failed',
                }
              : existingAttachment,
          ),
        );
      }
    },
    [analytics, chatUuid, setAttachments, upload],
  );

  /** Upload files from file input, drag/drop, or paste. */
  const uploadSelectedFiles = useCallback(
    async (files: FileList, source: Exclude<FileAttachmentSource, 'folder-picker'>) => {
      browserLogger.info(`[Nova] Processing files for upload`, {
        fileCount: files.length,
        fileNames: Array.from(files).map((f) => f.name),
        fileSizes: Array.from(files).map((f) => f.size),
      });

      const unsupportedFileTypes: string[] = [];
      const oversizedFiles: File[] = [];
      const supportedFiles: File[] = [];
      Array.from(files).forEach((file) => {
        const dotIndex = file.name.lastIndexOf('.');
        const fileExtension = dotIndex !== -1 ? file.name.slice(dotIndex).toLowerCase() : '';
        if (!CHAT_ALLOWED_ATTACHMENT_EXTENSIONS_LIST.includes(fileExtension)) {
          unsupportedFileTypes.push(fileExtension);
        } else if (file.size > MAX_FILE_SIZE_BYTES) {
          oversizedFiles.push(file);
        } else {
          supportedFiles.push(file);
        }
      });

      if (unsupportedFileTypes.length > 0) {
        unsupportedFileTypes.forEach((fileExtension) => {
          trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.CHAT_FILE_ATTACHMENT_FAILED, {
            chatUuid,
            fileExtension: fileExtension.replace(/^\./, '') || undefined,
            reason: 'unsupported_file_type',
            source,
          });
        });
        browserLogger.warn(`[Nova] Unsupported file types detected`, {
          unsupportedTypes: unsupportedFileTypes,
        });
        if (showNotifications) {
          addNotification({
            duration: 3000,
            title: `The following file types are not supported: ${unsupportedFileTypes.join(', ')}`,
            type: 'error',
          });
        }
      }

      if (oversizedFiles.length > 0) {
        oversizedFiles.forEach((file) => {
          trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.CHAT_FILE_ATTACHMENT_FAILED, {
            chatUuid,
            reason: 'file_too_large',
            source,
            ...getFileAnalyticsProperties(file),
          });
        });
        browserLogger.warn(`[Nova] Files exceed maximum size limit`, {
          fileNames: oversizedFiles.map((file) => file.name),
          maxSizeMB: MAX_FILE_SIZE_BYTES / (1024 * 1024),
        });
        if (showNotifications) {
          addNotification({
            duration: 5000,
            title: `The following files exceed the ${MAX_FILE_SIZE_MB}MB size limit: ${oversizedFiles.map((file) => file.name).join(', ')}`,
            type: 'error',
          });
        }
      }

      const uploadPromises = supportedFiles.map(async (file) => {
        const tempId = `${Date.now()}-${Math.random()}`;
        const newAttachment: FileAttachment = {
          file,
          isUploading: true,
          tempId,
        };

        await uploadFileAttachment({
          attachment: newAttachment,
          file,
          source,
          tempId,
        });
      });

      const results = await Promise.allSettled(uploadPromises);

      const successCount = results.filter((r) => r.status === 'fulfilled').length;
      const failureCount = results.filter((r) => r.status === 'rejected').length;

      browserLogger.info(`[Nova] All file uploads completed`, {
        totalFiles: supportedFiles.length,
        successCount,
        failureCount,
      });
    },
    [addNotification, analytics, chatUuid, showNotifications, uploadFileAttachment],
  );

  const handleFolderAttachmentClick = useCallback(async () => {
    let tempId: string | undefined;

    try {
      const directoryHandle = await pickLocalFolderDirectory();

      if (!directoryHandle) {
        return;
      }

      const folderTempId = `${Date.now()}-${Math.random()}`;
      tempId = folderTempId;
      const pendingArchiveFile = new File([], buildLocalFolderArchiveFilename(directoryHandle.name), {
        type: 'application/zip',
      });

      setAttachments((existingAttachments) => [
        ...existingAttachments,
        {
          file: pendingArchiveFile,
          kind: 'folder',
          folderName: directoryHandle.name,
          isUploading: true,
          processingStage: 'preparing',
          tempId: folderTempId,
        },
      ]);

      const bundle = await buildLocalFolderBundle(directoryHandle);
      const newAttachment: FileAttachment = {
        file: bundle.archiveFile,
        folderMetadata: bundle.metadata,
        kind: 'folder',
        isUploading: true,
        processingStage: 'uploading',
        tempId: folderTempId,
      };

      browserLogger.info('[Nova] Local folder bundled for upload', {
        archiveFileName: bundle.archiveFile.name,
        archiveSize: bundle.archiveFile.size,
        folderName: bundle.metadata.folderName,
        fileCount: bundle.metadata.fileCount,
        skippedFileCount: bundle.metadata.skippedFileCount,
        truncated: bundle.metadata.truncated,
      });

      await uploadFileAttachment({
        attachment: newAttachment,
        attachmentStateMode: 'replace',
        file: bundle.archiveFile,
        source: 'folder-picker',
        tempId: folderTempId,
      });
    } catch (error) {
      const reason =
        error instanceof LocalFolderPickerUnsupportedError ? 'unsupported_folder_picker' : 'folder_bundle_error';

      trackNovaEvent(analytics, NOVA_ANALYTICS_EVENTS.CHAT_FILE_ATTACHMENT_FAILED, {
        chatUuid,
        errorName: error instanceof Error ? error.name : undefined,
        reason,
        source: 'folder-picker',
      });

      browserLogger.error('[Nova] Local folder attachment failed', {
        error: error instanceof Error ? error.message : 'Unknown error',
        errorStack: error instanceof Error ? error.stack : undefined,
      });

      if (tempId) {
        setAttachments((existingAttachments) =>
          existingAttachments.map((existingAttachment) =>
            existingAttachment.tempId === tempId
              ? {
                  ...existingAttachment,
                  isUploading: false,
                  processingStage: undefined,
                  uploadError: 'Could not attach folder',
                }
              : existingAttachment,
          ),
        );
      }

      if (showNotifications) {
        addNotification({
          duration: 5000,
          title:
            error instanceof LocalFolderPickerUnsupportedError
              ? 'Folder attachments are not supported in this browser.'
              : 'Could not attach this folder.',
          type: 'error',
        });
      }
    }
  }, [addNotification, analytics, chatUuid, setAttachments, showNotifications, uploadFileAttachment]);

  const handleFileUpload = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = event.target;
      if (!files) return;

      browserLogger.info(`[Nova] Files selected via file input`, {
        fileCount: files.length,
        fileNames: Array.from(files).map((f) => f.name),
      });

      uploadSelectedFiles(files, 'button');

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    },
    [uploadSelectedFiles],
  );

  /** Handle drag and drop functionality */
  const handleDragOver = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (!isDragging) {
        browserLogger.info(`[Nova] User started dragging files over drop zone`);
      }
      setIsDragging(true);
    },
    [isDragging],
  );

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    browserLogger.info(`[Nova] User dragged files away from drop zone`);
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setIsDragging(false);

      const { files } = event.dataTransfer;

      browserLogger.info(`[Nova] Files dropped in drop zone`, {
        fileCount: files?.length || 0,
        fileNames: files ? Array.from(files).map((f) => f.name) : [],
      });

      if (isNotEmpty(files)) {
        uploadSelectedFiles(files, 'drag-drop');
      } else {
        browserLogger.warn(`[Nova] Drop event contained no files`);
      }
    },
    [uploadSelectedFiles],
  );

  /** Handle clipboard paste functionality */
  const handlePaste = useCallback(
    (event: React.ClipboardEvent) => {
      const files = event.clipboardData?.files;

      browserLogger.info(`[Nova] Files pasted from clipboard`, {
        fileCount: files?.length || 0,
        fileNames: files ? Array.from(files).map((f) => f.name) : [],
      });

      if (isNotEmpty(files)) {
        event.preventDefault();
        uploadSelectedFiles(files, 'paste');
      } else {
        browserLogger.debug(`[Nova] Paste event contained no files`);
      }
    },
    [uploadSelectedFiles],
  );

  /** When the attachment button is clicked, open the native file input */
  const handleAttachmentClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return {
    fileInputRef,
    isDragging,
    handleFileUpload,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handlePaste,
    handleAttachmentClick,
    handleFolderAttachmentClick,
    supportedFileTypes: CHAT_ALLOWED_ATTACHMENT_EXTENSIONS_LIST,
    uploadProgress,
  };
}
